import { useEffect } from "react";
import { Navigate, useLocation } from "react-router-dom";
import type { ReactNode } from "react";
import { useAuthStore } from "@/core/auth/auth-store";
import { useCurrentClubStore } from "@/core/auth/current-club-store";
import { useIsPlatformAdmin } from "@/core/auth/use-is-admin";
import { clubAccess } from "@/core/models/club";
import { usePreviewSession } from "@/features/preview-session/hooks/use-preview-session";
import { usePreviewClubReady } from "@/features/preview-session/hooks/use-preview-club-ready";
import { useUserClubs } from "../hooks/use-user-clubs";

interface Props {
  children: ReactNode;
}

/**
 * Sits between `<ProtectedRoute>` and `<AppShell>`. Ensures every screen below
 * has a `currentClubId` selected and that the selected club is in a usable
 * state. `_ClubGate` *
 * Membership routing:
 * - 0 memberships → `/no-club-access` (user can opt into `/onboarding`
 * from there, or sign out). Avoids auto-creating a parallel club for
 * staff who were just removed.
 * - 1 membership → auto-select, render children.
 * - 2+, none chosen → `/select-club`.
 * - 2+, one already chosen → render children.
 *
 * Club-status routing (non-admin only; platform admins bypass so they can
 * manage suspended clubs):
 * - `suspended` → `/suspended`.
 * - `pending_deletion` → `/pending-deletion`.
 */
export function ClubGate({ children }: Props) {
  const user = useAuthStore((s) => s.user);
  const clubId = useCurrentClubStore((s) => s.clubId);
  const setClubId = useCurrentClubStore((s) => s.setClubId);
  const location = useLocation();
  const previewSession = usePreviewSession();
  // Gates the preview shell on a confirmed token-backed read (see the preview
  // branch below). 'wait' until the club doc loads under the token.
  const previewShell = usePreviewClubReady();
  const { data: memberships, isPending, isError } = useUserClubs(user);
  const { isAdmin, isLoading: adminLoading } = useIsPlatformAdmin();
  // Auto-select when only one club is available. Runs in an effect because
  // mutating zustand during render triggers a React warning.
  useEffect(() => {
    if (!clubId && memberships && memberships.length === 1) {
      const only = memberships[0];
      if (only) setClubId(only.club.id);
    }
  }, [clubId, memberships, setClubId]);
  // Preview-session pin: the visitor is signed in via a redeemed token and
  // has no staff doc in any club. Force `currentClubId` to the token's
  // claim and skip membership / status gates entirely. Firestore rules
  // restrict reads to the matching `previewClubId`, so cross-club access
  // fails by default at the data layer.
  useEffect(() => {
    if (previewSession.active && previewSession.clubId && clubId !== previewSession.clubId) {
      setClubId(previewSession.clubId);
    }
  }, [previewSession.active, previewSession.clubId, clubId, setClubId]);
  if (previewSession.active && previewSession.clubId) {
    // Hold the shell — and therefore its child data queries — until the club
    // doc has actually loaded under the preview token. A successful read proves
    // the token's `previewClubId` claim is live on the Firestore client, so the
    // dashboard's bookings / courts / activity-log queries that mount next
    // can't fire into the sign-in → claim auth-race window (the "Missing or
    // insufficient permissions" red banner that surfaced intermittently).
    // React Query's retry rides out the warm-up; the static splash keeps
    // showing (we render null) meanwhile. Fall through on a terminal error so a
    // genuine failure shows the shell's own error state instead of hanging on
    // the splash forever.
    if (previewShell === "wait") return null;
    return <>{children}</>;
  }
  // Loading & error states render nothing — the static splash keeps showing
  // until a terminal page (AppShell or ClubPickerPage) calls `useAppReady`.
  // Wait for the preview-session lookup to resolve too — without this we'd
  // race past the gate on first paint when the visitor signed in via a
  // redeemed token, falling through to `/no-club-access` because the staff
  // membership query returns empty for preview-session UIDs.
  if (isPending || isError || adminLoading || previewSession.isLoading) return null;
  // Selected club is still in the user's memberships → check status gate
  // before rendering children.
  const activeMembership = clubId
    ? memberships?.find((m) => m.club.id === clubId)
    : undefined;
  if (activeMembership) {
    // Platform admins bypass the blocking status screens — they need access
    // to manage clubs in any state.
    if (!isAdmin) {
      // `blocked` = closed lifecycle OR a pending deletion (see clubAccess).
      // Either way, no app access — route to the right holding screen.
      if (clubAccess(activeMembership.club) === "blocked") {
        // `deletionRequestedAt` disambiguates the blocked state — owner-
        // initiated requests get the holding page; payment suspensions
        // get the reactivation prompt. Either way, no app access.
        const isPendingDeletion = activeMembership.club.deletionRequestedAt != null;
        if (
          isPendingDeletion &&
          location.pathname !== "/pending-deletion"
        ) {
          return <Navigate to="/pending-deletion" replace />;
        }
        if (
          !isPendingDeletion &&
          location.pathname !== "/suspended"
        ) {
          return <Navigate to="/suspended" replace />;
        }
      }
    }
    return <>{children}</>;
  }
  // Single-club auto-select is in flight: the useEffect above will set
  // `clubId` on the next tick. Return null (splash stays) so we don't
  // bounce the user to the picker in the meantime.
  if (memberships && memberships.length === 1) {
    return null;
  }
  // Zero memberships → no-club-access page. The "Crear un club nuevo" button
  // on that page routes to `/onboarding` deliberately.
  // Platform admins with zero memberships go to `/select-club` instead — the
  // picker switches to admin mode (lists every club) so they can manage any
  // tenant. Without this branch, the only platform admin without a staff
  // record gets stranded on the dead-end no-club-access screen.
  if (memberships && memberships.length === 0) {
    if (location.pathname === "/onboarding") {
      return <>{children}</>;
    }
    if (isAdmin) {
      if (location.pathname !== "/select-club") {
        return <Navigate to="/select-club" replace />;
      }
      return <>{children}</>;
    }
    if (location.pathname !== "/no-club-access") {
      return <Navigate to="/no-club-access" replace />;
    }
    return <>{children}</>;
  }
  // 2+ memberships, none chosen → picker handles the UX.
  if (location.pathname !== "/select-club") {
    return <Navigate to="/select-club" replace />;
  }
  return <>{children}</>;
}
